<template>
  <div class="opensource">
    <!-- ===== Hero ===== -->
    <section class="os-hero grid-bg">
      <div class="glow-orb" style="background: var(--accent); top: -120px; left: -60px; width: 460px; height: 460px;"></div>
      <div class="glow-orb" style="background: var(--lime); bottom: -100px; right: -40px; width: 360px; height: 360px; opacity: 0.2;"></div>

      <div class="container os-hero-inner">
        <div class="os-hero-badge" data-aos="fade-up">
          <span class="os-hero-dot"></span>
          <i class="fa-solid fa-code-branch"></i>
          OPEN SOURCE · 4 PROJECTS
        </div>

        <h1 class="os-hero-title" data-aos="fade-up" data-aos-delay="80">
          四大 <span class="os-accent">开源工具库</span>
        </h1>
        <p class="os-hero-desc" data-aos="fade-up" data-aos-delay="140">
          从 Kotlin 扩展方法到串口通信，从工具集合到 UVC 摄像头，覆盖 Android 开发高频场景。
          均发布至 Maven 中央仓库，长期维护，欢迎 Star 与 Issue。
        </p>

        <div class="os-hero-stats" data-aos="fade-up" data-aos-delay="200">
          <div class="os-hero-stat">
            <i class="fa-solid fa-cubes"></i>
            <span><strong>4</strong> 个核心库</span>
          </div>
          <div class="os-hero-stat">
            <i class="fa-brands fa-android"></i>
            <span><strong>Android</strong> 全场景</span>
          </div>
          <div class="os-hero-stat">
            <i class="fa-solid fa-cube"></i>
            <span><strong>Maven</strong> 中央仓库</span>
          </div>
          <div class="os-hero-stat">
            <i class="fa-solid fa-scale-balanced"></i>
            <span><strong>Apache-2.0</strong> 开源协议</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== 项目卡片 ===== -->
    <section class="section">
      <div class="container">
        <div class="os-list-head" data-aos="fade-up">
          <span class="section-eyebrow">PROJECTS</span>
          <h2 class="section-title">选择一个项目查看详情</h2>
          <p class="section-subtitle">每个项目都是独立维护的开源仓库，包含完整文档、示例代码与 Gradle 引入方式</p>
        </div>

        <div class="os-grid">
          <router-link
            v-for="(p, i) in projectCards"
            :key="p.key"
            :to="p.to"
            class="os-card"
            data-aos="fade-up"
            :data-aos-delay="i * 80"
          >
            <div class="os-card-glow" :style="{ background: p.color }"></div>
            <div class="os-card-head">
              <div class="os-card-icon" :style="{ background: p.color + '1f', color: p.color, borderColor: p.color + '44' }">
                <i :class="p.icon"></i>
              </div>
              <a :href="p.repo" target="_blank" rel="noopener" class="os-card-ext" @click.stop title="前往仓库">
                <i class="fa-solid fa-arrow-up-right-from-square"></i>
              </a>
            </div>

            <h3 class="os-card-name">{{ p.name }}</h3>
            <p class="os-card-tagline">{{ p.tagline }}</p>

            <div class="os-card-tags">
              <span v-for="t in p.tags" :key="t" class="os-card-tag">{{ t }}</span>
            </div>

            <div class="os-card-meta">
              <span class="os-card-lang">
                <i class="fa-solid fa-code"></i> {{ p.language }}
              </span>
            </div>

            <div class="os-card-cta">
              查看详情 <i class="fa-solid fa-arrow-right"></i>
            </div>
          </router-link>
        </div>
      </div>
    </section>

    <!-- ===== 共性特点 ===== -->
    <section class="section os-features-section">
      <div class="container">
        <div data-aos="fade-up">
          <span class="section-eyebrow">WHY USE</span>
          <h2 class="section-title">四个库的共同特点</h2>
          <p class="section-subtitle">长期维护、兼容性强、文档完善、开箱即用</p>
        </div>

        <div class="os-features-grid">
          <div v-for="(f, i) in commonFeatures" :key="f.title" class="os-feature" data-aos="fade-up" :data-aos-delay="i * 60">
            <div class="os-feature-icon" :style="{ color: f.color, background: f.color + '14', borderColor: f.color + '44' }">
              <i :class="f.icon"></i>
            </div>
            <h3 class="os-feature-title">{{ f.title }}</h3>
            <p class="os-feature-desc">{{ f.desc }}</p>
          </div>
        </div>
      </div>
    </section>

    <!-- ===== CTA ===== -->
    <section class="section">
      <div class="container">
        <div class="os-cta-box" data-aos="fade-up">
          <div class="glow-orb" style="background: var(--accent); top: -40px; left: 30%; width: 300px; height: 300px; opacity: 0.2;"></div>
          <div class="os-cta-inner">
            <span class="section-eyebrow">GET STARTED</span>
            <h2 class="section-title">一行代码引入，立即使用</h2>
            <p class="section-subtitle">所有项目均发布至 Maven 中央仓库，Gradle 一行引入即可使用，详见各项目详情页。</p>
            <div class="os-cta-actions">
              <a :href="site.github" target="_blank" rel="noopener" class="btn btn-primary">
                <i class="fa-brands fa-github"></i> GitHub 主页
              </a>
              <router-link to="/resume" class="btn btn-ghost">
                <i class="fa-solid fa-file-lines"></i> 查看简历
              </router-link>
            </div>
          </div>
        </div>
      </div>
    </section>
  </div>
</template>

<script setup>
import { projectCards } from '@/data/projects.js'
import { site } from '@/data/site.js'

const commonFeatures = [
  { icon: 'fa-solid fa-rocket', title: '开箱即用', desc: 'Gradle 一行引入，初始化后即可调用，无需复杂配置', color: '#22D3EE' },
  { icon: 'fa-solid fa-shield-halved', title: '稳定可靠', desc: '历经多个生产项目长期验证，兼容 Android 4.4 ~ 14', color: '#34D399' },
  { icon: 'fa-solid fa-book', title: '文档完善', desc: '每个项目附带完整 README、示例代码与使用说明', color: '#38BDF8' },
  { icon: 'fa-solid fa-arrows-rotate', title: '长期维护', desc: '持续更新迭代，欢迎 Issue 与 PR 共建生态', color: '#818CF8' }
]
</script>

<style scoped>
/* ===== Hero ===== */
.os-hero {
  position: relative;
  padding: 100px 0 80px;
  overflow: hidden;
  min-height: 60vh;
  display: flex;
  align-items: center;
}
.os-hero-inner { position: relative; z-index: 1; max-width: 820px; }
.os-hero-badge {
  display: inline-flex;
  align-items: center;
  gap: 10px;
  padding: 8px 16px;
  border: 1px solid var(--border);
  border-radius: 100px;
  font-family: var(--font-mono);
  font-size: 0.8rem;
  color: var(--text-muted);
  background: var(--bg-surface);
  margin-bottom: 28px;
}
.os-hero-dot {
  width: 8px; height: 8px;
  background: var(--accent);
  border-radius: 50%;
  box-shadow: 0 0 8px var(--accent);
  animation: float 2s ease-in-out infinite;
}
.os-hero-title {
  font-family: var(--font-display);
  font-weight: 700;
  font-size: clamp(2.8rem, 7vw, 5rem);
  line-height: 1.05;
  letter-spacing: -0.03em;
  margin-bottom: 24px;
}
.os-accent { color: var(--accent); }
.os-hero-desc {
  color: var(--text-muted);
  font-size: 1.08rem;
  line-height: 1.8;
  max-width: 640px;
  margin-bottom: 40px;
}
.os-hero-stats {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 16px;
  max-width: 720px;
}
.os-hero-stat {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 14px 16px;
  background: var(--bg-surface);
  border: 1px solid var(--border-soft);
  border-radius: 12px;
  font-size: 0.88rem;
  color: var(--text-muted);
  transition: all 0.25s ease;
}
.os-hero-stat:hover {
  border-color: var(--accent);
  transform: translateY(-2px);
}
.os-hero-stat i {
  font-size: 1.1rem;
  color: var(--accent);
}
.os-hero-stat strong {
  color: var(--text);
  font-weight: 600;
}

/* ===== 列表头部 ===== */
.os-list-head { margin-bottom: 56px; }

/* ===== 项目卡片 ===== */
.os-grid {
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  gap: 24px;
}
.os-card {
  position: relative;
  display: block;
  padding: 32px 28px 24px;
  background: var(--bg-surface);
  border: 1px solid var(--border-soft);
  border-radius: 18px;
  text-decoration: none;
  color: inherit;
  overflow: hidden;
  transition: all 0.3s ease;
}
.os-card-glow {
  position: absolute;
  top: -60px;
  right: -40px;
  width: 200px;
  height: 200px;
  border-radius: 50%;
  opacity: 0.1;
  filter: blur(60px);
  transition: opacity 0.3s ease;
}
.os-card:hover {
  border-color: var(--accent);
  transform: translateY(-4px);
  box-shadow: 0 20px 50px rgba(0, 0, 0, 0.3);
}
.os-card:hover .os-card-glow { opacity: 0.22; }
.os-card-head {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 20px;
}
.os-card-icon {
  width: 56px; height: 56px;
  display: grid; place-items: center;
  border-radius: 14px;
  font-size: 1.5rem;
  border: 1px solid var(--border);
}
.os-card-ext {
  width: 36px; height: 36px;
  display: grid; place-items: center;
  color: var(--text-dim);
  border-radius: 8px;
  transition: all 0.2s ease;
}
.os-card-ext:hover {
  color: var(--accent);
  background: var(--bg-elevated);
}
.os-card-name {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.6rem;
  margin-bottom: 8px;
}
.os-card-tagline {
  color: var(--text-muted);
  font-size: 0.95rem;
  line-height: 1.6;
  margin-bottom: 18px;
}
.os-card-tags {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  margin-bottom: 18px;
}
.os-card-tag {
  padding: 4px 10px;
  background: var(--bg-elevated);
  border: 1px solid var(--border-soft);
  border-radius: 6px;
  font-size: 0.76rem;
  color: var(--text-muted);
  font-family: var(--font-mono);
}
.os-card-meta {
  padding-top: 16px;
  border-top: 1px solid var(--border-soft);
  font-size: 0.82rem;
  color: var(--text-dim);
  font-family: var(--font-mono);
}
.os-card-lang {
  display: inline-flex;
  align-items: center;
  gap: 6px;
}
.os-card-cta {
  margin-top: 16px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  font-size: 0.9rem;
  font-weight: 600;
  color: var(--text-muted);
  transition: all 0.25s ease;
}
.os-card:hover .os-card-cta {
  color: var(--accent);
  gap: 12px;
}

/* ===== 共性特点 ===== */
.os-features-section { background: var(--bg-surface); }
.os-features-grid {
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 20px;
  margin-top: 48px;
}
.os-feature {
  padding: 28px 24px;
  background: var(--bg-base);
  border: 1px solid var(--border-soft);
  border-radius: 16px;
  transition: all 0.3s ease;
}
.os-feature:hover {
  transform: translateY(-4px);
  border-color: var(--accent);
}
.os-feature-icon {
  width: 48px; height: 48px;
  display: grid; place-items: center;
  border-radius: 12px;
  font-size: 1.3rem;
  border: 1px solid var(--border);
  margin-bottom: 18px;
}
.os-feature-title {
  font-family: var(--font-display);
  font-weight: 600;
  font-size: 1.1rem;
  margin-bottom: 8px;
}
.os-feature-desc {
  color: var(--text-muted);
  font-size: 0.88rem;
  line-height: 1.6;
}

/* ===== CTA ===== */
.os-cta-box {
  position: relative;
  overflow: hidden;
  background: var(--bg-surface);
  border: 1px solid var(--border-soft);
  border-radius: 24px;
  padding: 56px 48px;
  text-align: center;
}
.os-cta-inner { position: relative; z-index: 1; }
.os-cta-actions {
  display: flex;
  justify-content: center;
  gap: 14px;
  flex-wrap: wrap;
  margin-top: 28px;
}

/* ===== 响应式 ===== */
@media (max-width: 900px) {
  .os-grid { grid-template-columns: 1fr; }
  .os-features-grid { grid-template-columns: repeat(2, 1fr); }
  .os-hero-stats { grid-template-columns: repeat(2, 1fr); }
}
@media (max-width: 560px) {
  .os-features-grid { grid-template-columns: 1fr; }
  .os-cta-box { padding: 40px 24px; }
}
</style>
